import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShoppingCart, Save, Info, Hammer, ArrowRight, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import PVCNotifyForm from "../components/PVCNotifyForm";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import EnclosureViewer from "../components/EnclosureViewer";
import SpeciesWizard from "../components/SpeciesWizard";
import ShareConfigButton from "../components/ShareConfigButton";
import BuildQueueBadge from "../components/BuildQueueBadge";
import KlarnaBadge from "../components/KlarnaBadge";
import BundleUpsell from "../components/BundleUpsell";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fixed sizes — mirror of backend ENGINEERED_SIZES. Hardcoded on the client so the size grid
// always renders even if the API is slow or unreachable (poor connection, cold start, etc.).
const ENGINEERED_SIZES = {
  "2x2x2": { width: 2, height: 2, depth: 2, name: "2ft W x 2ft H x 2ft D" },
  "3x2x2": { width: 3, height: 2, depth: 2, name: "3ft W x 2ft H x 2ft D" },
  "4x2x2": { width: 4, height: 2, depth: 2, name: "4ft W x 2ft H x 2ft D" },
  "5x2x2": { width: 5, height: 2, depth: 2, name: "5ft W x 2ft H x 2ft D" },
  "6x2x2": { width: 6, height: 2, depth: 2, name: "6ft W x 2ft H x 2ft D" },
  "2x3x2": { width: 2, height: 3, depth: 2, name: "2ft W x 3ft H x 2ft D" },
  "3x3x2": { width: 3, height: 3, depth: 2, name: "3ft W x 3ft H x 2ft D" },
  "4x3x2": { width: 4, height: 3, depth: 2, name: "4ft W x 3ft H x 2ft D" },
  "5x3x2": { width: 5, height: 3, depth: 2, name: "5ft W x 3ft H x 2ft D" },
  "6x3x2": { width: 6, height: 3, depth: 2, name: "6ft W x 3ft H x 2ft D" },
  "2x4x2": { width: 2, height: 4, depth: 2, name: "2ft W x 4ft H x 2ft D" },
  "3x4x2": { width: 3, height: 4, depth: 2, name: "3ft W x 4ft H x 2ft D" },
  "4x4x2": { width: 4, height: 4, depth: 2, name: "4ft W x 4ft H x 2ft D" },
  "5x4x2": { width: 5, height: 4, depth: 2, name: "5ft W x 4ft H x 2ft D" },
  "6x4x2": { width: 6, height: 4, depth: 2, name: "6ft W x 4ft H x 2ft D" },
};

const EngineeredConfiguratorPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [sizes] = useState(ENGINEERED_SIZES);
  const [config, setConfig] = useState({
    size_key: "4x3x2",
    glass_thickness: 4,
  });
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedSpeciesId, setSelectedSpeciesId] = useState(null);

  // Hydrate config from URL params on mount (Share-this-build deep links).
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const hydration = {};
    const size = searchParams.get("size") || searchParams.get("size_key");
    const glass = searchParams.get("glass") || searchParams.get("glass_thickness");
    if (size && ENGINEERED_SIZES[size]) hydration.size_key = size;
    if (glass && [4, 6, 8, 10].includes(Number(glass))) hydration.glass_thickness = Number(glass);
    if (Object.keys(hydration).length > 0) {
      setConfig((prev) => ({ ...prev, ...hydration }));
      toast.success("Loaded shared build configuration");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Material comparison: always quoted at the same reference size (4x2x2) so budget
  // customers can see the price gap between Engineered, Wood and PVC at a glance.
  const COMPARE_SIZE = { width: 4, height: 2, depth: 2, key: "4x2x2" };
  const [compare, setCompare] = useState({ wood: null, engineered: null, pvc: null });
  useEffect(() => {
    const fetchCompare = async () => {
      try {
        const [woodRes, engRes, pvcRes] = await Promise.all([
          axios.post(`${API}/pricing/calculate`, {
            shape: "rectangular",
            width: COMPARE_SIZE.width,
            height: COMPARE_SIZE.height,
            depth: COMPARE_SIZE.depth,
            stain_color: "natural",
            glass_thickness: 6,
          }),
          axios.post(`${API}/pricing/calculate-engineered`, {
            size_key: COMPARE_SIZE.key,
            glass_thickness: 4,
          }),
          axios.post(`${API}/calculate-pvc-price`, {
            width: COMPARE_SIZE.width,
            height: COMPARE_SIZE.height,
            depth: COMPARE_SIZE.depth,
            pvc_color: "white",
          }),
        ]);
        setCompare({
          wood: woodRes.data?.total ?? null,
          engineered: engRes.data?.total ?? null,
          pvc: pvcRes.data?.total ?? null,
        });
      } catch (e) {
        console.error("Compare fetch error:", e);
      }
    };
    fetchCompare();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const calc = async () => {
      setPricingError(false);
      try {
        const res = await axios.post(`${API}/pricing/calculate-engineered`, config);
        if (!cancelled) setPricing(res.data);
      } catch (e) {
        console.error("Engineered pricing error:", e?.message || e);
        if (!cancelled) {
          setPricing(null);
          setPricingError(true);
        }
      }
    };
    calc();
    return () => {
      cancelled = true;
    };
  }, [config]);

  const retryPricing = async () => {
    setPricingError(false);
    try {
      const res = await axios.post(`${API}/pricing/calculate-engineered`, config);
      setPricing(res.data);
      toast.success("Price updated");
    } catch (e) {
      console.error("Retry pricing error:", e?.message || e);
      setPricingError(true);
      toast.error("Still can't reach our servers. Check your connection and try again.");
    }
  };

  const currentSize = sizes[config.size_key];
  const viewerConfig = {
    shape: "rectangular",
    width: currentSize?.width || 4,
    height: currentSize?.height || 3,
    depth: currentSize?.depth || 2,
    stain_color: "natural",
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error("Please login to add items to cart");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/cart/add-engineered`,
        { engineered_config: config, quantity: 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Engineered enclosure added to cart!");
    } catch (e) {
      toast.error("Failed to add to cart");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!user) {
      toast.error("Please login to save configurations");
      navigate("/login");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/configurations/save`,
        { ...viewerConfig, type: "engineered", size_key: config.size_key, glass_thickness: config.glass_thickness },
        {
          params: { name: `Engineered ${currentSize?.name || config.size_key}` },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Configuration saved!");
    } catch (e) {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleWizardApply = (rec) => {
    setConfig((prev) => ({
      ...prev,
      size_key: rec.size_key,
      glass_thickness: rec.glass_thickness,
    }));
    setSelectedSpeciesId(rec.speciesId || null);
    if (rec.overflow) {
      toast.warning(
        `${rec.speciesName} needs more space than our Engineered range — consider the Wood tier.`
      );
    } else {
      toast.success(`Applied recommended setup for ${rec.speciesName}`);
    }
  };

  return (
    <div className="min-h-screen pt-16 bg-[#0A0A0A]">
      {/* Header */}
      <div className="bg-gradient-to-b from-amber-900/30 to-transparent py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Hammer className="w-5 h-5 text-amber-400" />
            </div>
            <h1 className="text-3xl font-bold font-['Space_Grotesk'] text-[#F4F1DE]">
              Engineered Budget Enclosures
            </h1>
            <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs font-semibold rounded">BUDGET</span>
          </div>
          <p className="text-[#F4F1DE]/60 ml-13">
            Solid OSB3 engineered board — a wallet-friendly build in fixed sizes. Finished with a natural raw-board look.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 3D Viewer */}
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
            <div className="aspect-[4/3]">
              <EnclosureViewer config={viewerConfig} />
            </div>
          </div>

          {/* Controls */}
          <div
            className="bg-[#121212] border border-white/10 rounded-lg p-6 overflow-y-auto"
            data-testid="engineered-configurator-controls"
          >
            {/* Build queue badge */}
            <BuildQueueBadge />

            {/* Species Wizard */}
            <div className="mb-3">
              <SpeciesWizard variant="engineered" onApply={handleWizardApply} />
            </div>
            <div className="mb-6">
              <ShareConfigButton variant="engineered" config={config} summary={`an Engineered enclosure (${config.size_key})`} />
            </div>

            {/* Price Display */}
            <div
              className="mb-6 p-4 bg-[#0A0A0A] border border-white/10 rounded-lg"
              data-testid="engineered-price-display"
            >
              <p className="text-[#F4F1DE]/60 text-sm uppercase tracking-widest mb-2">Total Price</p>
              <p
                className="text-4xl font-bold font-['Space_Grotesk'] text-amber-400"
                data-testid="engineered-total-price"
              >
                {pricing?.total != null ? `£${pricing.total.toFixed(2)}` : pricingError ? "—" : "..."}
              </p>
              <KlarnaBadge totalPrice={pricing?.total} />
              {pricingError && !pricing && (
                <button
                  onClick={retryPricing}
                  className="mt-2 text-xs text-amber-400 hover:text-amber-300 underline"
                  data-testid="engineered-retry-pricing"
                >
                  Couldn't load price — tap to retry
                </button>
              )}
              {pricing && (
                <div className="mt-4 space-y-1 text-sm text-[#F4F1DE]/50">
                  <div className="flex justify-between">
                    <span>OSB3 Board</span>
                    <span>£{pricing.osb3_cost?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Glass</span>
                    <span>£{pricing.glass_cost?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Labour</span>
                    <span>£{pricing.labor_cost?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <span>Subtotal</span>
                    <span>£{pricing.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>VAT (20%)</span>
                    <span>£{pricing.vat?.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Bundle Upsell */}
            <BundleUpsell selectedSpeciesId={selectedSpeciesId} defaultKit="fossorial_gecko" />

            {/* Size */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-[#F4F1DE] mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-amber-500/20 rounded flex items-center justify-center text-xs text-amber-400">
                  1
                </span>
                Size (Width × Height × Depth)
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                {Object.entries(sizes).map(([key, s]) => (
                  <button
                    key={key}
                    onClick={() => setConfig((prev) => ({ ...prev, size_key: key }))}
                    className={`text-left px-3 py-2 rounded border transition-all ${
                      config.size_key === key
                        ? "border-amber-500 bg-amber-500/10 text-amber-300"
                        : "border-white/10 hover:border-white/30 text-[#F4F1DE]/80"
                    }`}
                    data-testid={`engineered-size-${key}`}
                  >
                    <span className="font-mono text-sm">{s.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Glass Thickness */}
            <div className="mb-8">
              <h2 className="text-lg font-bold text-[#F4F1DE] mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-amber-500/20 rounded flex items-center justify-center text-xs text-amber-400">
                  2
                </span>
                Glass Thickness
              </h2>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { mm: 4, tag: "Standard", blurb: "Small species" },
                  { mm: 6, tag: "Upgrade", blurb: "Dragons, snakes" },
                  { mm: 8, tag: "Upgrade", blurb: "Monitors, tegus" },
                  { mm: 10, tag: "Upgrade", blurb: "Heavy-duty" },
                ].map(({ mm, tag, blurb }) => (
                  <button
                    key={mm}
                    onClick={() => setConfig((prev) => ({ ...prev, glass_thickness: mm }))}
                    className={`py-2 px-1.5 rounded border transition-all relative flex flex-col gap-1 items-center justify-start ${
                      config.glass_thickness === mm
                        ? "border-amber-500 bg-amber-500/10 text-amber-300"
                        : "border-white/10 hover:border-white/30 text-[#F4F1DE]/80"
                    }`}
                    data-testid={`engineered-glass-${mm}`}
                  >
                    <span className="font-semibold">{mm}mm</span>
                    <span className={`text-[9px] leading-tight ${
                      config.glass_thickness === mm ? "text-amber-300/80" : "text-[#F4F1DE]/40"
                    }`}>
                      {blurb}
                    </span>
                    <span
                      className={`absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                        tag === "Standard" ? "bg-amber-500 text-black" : "bg-white/20 text-[#F4F1DE]"
                      }`}
                    >
                      {tag}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Info note */}
            <div className="mb-6 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-xs text-[#F4F1DE]/70 leading-relaxed">
                Engineered enclosures are built from structural OSB3 board and supplied with the natural raw-board
                finish — no stain or colour options. Ideal for budget builds, rack setups and quarantine vivs.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={handleAddToCart}
                disabled={loading || !pricing}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                data-testid="engineered-add-to-cart-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                {loading ? "Adding..." : "Add to Cart"}
              </Button>
              <Button
                onClick={handleSaveConfig}
                disabled={saving}
                variant="outline"
                className="border-white/20 text-[#F4F1DE] hover:bg-white/5"
                data-testid="engineered-save-config-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </div>

        {/* Compare the range — same 4×2×2 size across Wood / Engineered / PVC */}
        <section className="mt-14" data-testid="compare-materials-panel">
          <div className="mb-8 text-center">
            <p className="text-xs uppercase tracking-[0.25em] text-amber-400/70 mb-2">At-a-glance</p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Space_Grotesk'] text-[#F4F1DE]">
              Compare the range
            </h2>
            <p className="text-[#F4F1DE]/55 mt-2">
              Like-for-like pricing on a 4ft W × 2ft H × 2ft D build — see where your money goes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Engineered card (this page) */}
            <div
              className="rounded-lg border border-amber-500/40 bg-gradient-to-b from-amber-500/10 to-[#121212] p-6 relative overflow-hidden"
              data-testid="compare-card-engineered"
            >
              <div className="absolute top-3 right-3 text-[10px] uppercase tracking-wider bg-amber-500 text-black font-semibold px-2 py-0.5 rounded">
                Budget
              </div>
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center mb-4">
                <Hammer className="w-5 h-5 text-amber-400" />
              </div>
              <h3 className="text-lg font-bold text-[#F4F1DE] mb-1">Engineered</h3>
              <p className="text-[#F4F1DE]/50 text-xs mb-4">OSB3 structural board · natural finish</p>
              <p className="text-3xl font-bold font-['Space_Grotesk'] text-amber-400 mb-4" data-testid="compare-price-engineered">
                {compare.engineered != null ? `£${compare.engineered.toFixed(2)}` : "—"}
              </p>
              {compare.engineered != null && compare.wood != null && compare.wood > compare.engineered && (
                <div
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-green-500/15 border border-green-500/30 text-green-300 text-xs font-semibold mb-4"
                  data-testid="compare-savings-badge"
                >
                  <span className="text-[10px] uppercase tracking-wider opacity-80">Save</span>
                  £{(compare.wood - compare.engineered).toFixed(2)} vs Wood
                </div>
              )}
              <ul className="text-xs text-[#F4F1DE]/60 space-y-1.5 mb-5">
                <li>• 15 fixed sizes</li>
                <li>• Same glass & equipment options</li>
                <li>• No stain — raw OSB finish</li>
              </ul>
              <Button
                disabled
                className="w-full bg-amber-500/30 text-amber-200 font-semibold cursor-default"
              >
                You're viewing this
              </Button>
            </div>

            {/* Wood card */}
            <div
              className="rounded-lg border border-[#E07A5F]/40 bg-gradient-to-b from-[#E07A5F]/10 to-[#121212] p-6 relative overflow-hidden"
              data-testid="compare-card-wood"
            >
              <div className="absolute top-3 right-3 text-[10px] uppercase tracking-wider bg-[#E07A5F] text-white font-semibold px-2 py-0.5 rounded">
                Premium
              </div>
              <div className="w-10 h-10 bg-[#E07A5F]/20 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-[#E07A5F]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16v16H4z M4 9h16 M9 4v16" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#F4F1DE] mb-1">Wood</h3>
              <p className="text-[#F4F1DE]/50 text-xs mb-4">18mm Marine plywood · 25 stain colours</p>
              <p className="text-3xl font-bold font-['Space_Grotesk'] text-[#E07A5F] mb-4" data-testid="compare-price-wood">
                {compare.wood != null ? `£${compare.wood.toFixed(2)}` : "—"}
              </p>
              <ul className="text-xs text-[#F4F1DE]/60 space-y-1.5 mb-5">
                <li>• Fully configurable dimensions</li>
                <li>• 25 water-based stain colours</li>
                <li>• 3 shapes — rect, corner, hex</li>
              </ul>
              <Link to="/configurator">
                <Button className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white font-semibold" data-testid="compare-cta-wood">
                  Upgrade to Wood
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* PVC card (out of stock) */}
            <div
              className="rounded-lg border border-white/10 bg-[#121212] p-6 relative overflow-hidden opacity-70"
              data-testid="compare-card-pvc"
            >
              <div className="absolute top-3 right-3 text-[10px] uppercase tracking-wider bg-red-500/80 text-white font-semibold px-2 py-0.5 rounded">
                Out of stock
              </div>
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center mb-4">
                <Lock className="w-5 h-5 text-[#F4F1DE]/50" />
              </div>
              <h3 className="text-lg font-bold text-[#F4F1DE] mb-1">PVC</h3>
              <p className="text-[#F4F1DE]/50 text-xs mb-4">Waterproof PVC · acrylic front</p>
              <p className="text-3xl font-bold font-['Space_Grotesk'] text-[#F4F1DE]/60 mb-4" data-testid="compare-price-pvc">
                {compare.pvc != null ? `£${compare.pvc.toFixed(2)}` : "—"}
              </p>
              <ul className="text-xs text-[#F4F1DE]/50 space-y-1.5 mb-5">
                <li>• White or Black only</li>
                <li>• 100% waterproof panels</li>
                <li>• Restocking soon</li>
              </ul>
              <PVCNotifyForm variant="inline" />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default EngineeredConfiguratorPage;
